<template>
  <main class="tm-v2-page">
    <user-workload-to-company-modal />
    <incurred-expense-modal />
    <task-made-form-modal
      ref="formModal"
      :tasks="tasks"
      :task-types="taskTypes"
      :customers="customers"
      @saved="onTaskSaved"
      @date-changed="onDateChanged"
    />

    <div class="container-fluid py-4">
      <div class="tm-v2-header mb-4">
        <div>
          <h1 class="tm-v2-title">Lançamentos de Atividades</h1>
          <p class="tm-v2-subtitle">Gerencie seu tempo e registros com precisão</p>
        </div>
        <button class="btn btn-new-task shadow-sm" @click="openCreateModal">
          <i class="fas fa-plus me-2"></i> Novo Lançamento
        </button>
      </div>

      <!-- Barra de workload -->
      <workload-bar
        :date="date"
        :workload="workload"
        @resume="openWorkloadResume"
      />

      <!-- Tickets em aberto -->
      <open-tickets-panel
        v-if="customerServices.length"
        :tickets="customerServices"
        :active-ticket-id="customerServiceId"
        @start="startCustomerService"
        @start-fixed="startCustomerServiceStatus"
        @finish="finishedCustomerService"
        @expense="openIncurredExpense"
      />

      <!-- Filtros -->
      <tasks-made-filters
        :tasks="tasks"
        :task-types="taskTypes"
        :customers="customers"
        @filter="applyFilters"
        @clear="clearFilters"
      />

      <!-- Tabela -->
      <tasks-made-table
        :tasks-made="tasksMade"
        :tasks="tasks"
        :task-types="taskTypes"
        :customers="customers"
        :total-spent="total_spent"
        :loading="loading"
        @edit="editTaskMade"
        @delete="destroy"
        @replay="replay"
      />
    </div>
  </main>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import { mapGetters } from 'vuex'
import WorkloadBar from '@/components/TasksMadeV2/WorkloadBar.vue'
import TaskMadeFormModal from '@/components/TasksMadeV2/TaskMadeFormModal.vue'
import TasksMadeFilters from '@/components/TasksMadeV2/TasksMadeFilters.vue'
import OpenTicketsPanel from '@/components/TasksMadeV2/OpenTicketsPanel.vue'
import TasksMadeTable from '@/components/TasksMadeV2/TasksMadeTable.vue'
import UserWorkloadToCompanyModal from '@/components/UserWorkloadToCompaniesModal.vue'
import IncurredExpenseModal from '@/components/IncurredExpenseModal.vue'

export default {
  name: 'TasksMadeV2',
  components: {
    WorkloadBar,
    TaskMadeFormModal,
    TasksMadeFilters,
    OpenTicketsPanel,
    TasksMadeTable,
    UserWorkloadToCompanyModal,
    IncurredExpenseModal
  },
  data() {
    return {
      eventHub: new Vue(),
      tasks: [],
      taskTypes: [],
      customers: [],
      errors: {},
      date: new Date(),
      tasksMade: [],
      workload: {
        worked_total: 0,
        workload_total: 0,
        percentage_worked: 0,
        tolerance: 0
      },
      total_spent: 0,
      customerServices: [],
      customerServiceId: null,
      filters: {},
      stringFilters: null,
      loadStringFilters: false,
      searchTimeout: null,
      loading: false,
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user'
    })
  },
  provide() {
    return {
      eventHub: this.eventHub
    }
  },
  mounted() {
    this.loadFormOptions()
    this.onDateChanged(this.date)
    this.loadCustomerServices()
  },
  methods: {
    async loadFormOptions() {
      try {
        const [tasksRes, typesRes, customersRes] = await Promise.all([
          axios.get('/api/tasks'),
          axios.get('/api/task_types'),
          axios.get('/api/customers')
        ])
        this.tasks = tasksRes.data
        this.taskTypes = typesRes.data.data
        this.customers = customersRes.data.data
      } catch (error) {
        this.$toast.error('Erro ao carregar opções do formulário')
      }
    },

    async loadCustomerServices() {
      try {
        const { data } = await axios.get(`/api/customer-service?userId=${this.user.id}&task-made=1`)
        this.customerServices = data.data
      } catch (error) {
        console.error(error)
      }
    },

    async index() {
      this.loading = true
      if (!this.loadStringFilters) {
        this.stringFilters = `startDate=${this.formatDate(this.date)}&endDate=${this.formatDate(this.date)}`
      }
      try {
        const { data } = await axios.get(`/api/tasks_made?${this.stringFilters}`)
        this.tasksMade = data.data.map(t => ({
          ...t,
          made_at: new Date(t.made_at + ' 12:00:00')
        }))
        this.total_spent = this.tasksMade.reduce((acc, t) => acc + t.time_spent, 0)
      } catch (error) {
        this.$toast.error('Erro ao carregar atividades')
      } finally {
        this.loading = false
      }
    },

    async loadWorkload() {
      try {
        const dateStr = this.formatDate(this.date)
        const { data } = await axios.get(`/api/tasks_made/workload?startDate=${dateStr}&endDate=${dateStr}`)
        this.workload = data
        
        if (this.isToday(this.date) && this.workload.max_hour) {
          this.$refs.formModal.setStartedAt(this.workload.max_hour.finished_at)
        }
      } catch (error) {
        console.error(error)
      }
    },

    openCreateModal() {
      this.$refs.formModal.open()
    },

    onTaskSaved() {
      this.index()
      this.loadWorkload()
      if (this.customerServiceId) {
        this.loadCustomerServices()
        this.customerServiceId = null
      }
      this.$toast.success('Atividade lançada com sucesso!')
    },

    onDateChanged(newDate) {
      this.date = newDate
      this.loadStringFilters = false
      this.index()
      this.loadWorkload()
    },

    applyFilters(filters) {
      this.filters = filters
      let query = `startDate=${this.formatDate(filters.startDate)}&endDate=${this.formatDate(filters.endDate)}`
      if (filters.task) query += `&task=${filters.task}`
      if (filters.taskType) query += `&taskType=${filters.taskType}`
      if (filters.customer) query += `&customer=${filters.customer}`
      if (filters.note) query += `&note=${filters.note}`
      
      this.stringFilters = query
      this.loadStringFilters = true
      this.index()
    },

    clearFilters() {
      this.loadStringFilters = false
      this.onDateChanged(this.date)
    },

    startCustomerService(ticket) {
      this.customerServiceId = ticket.id
      this.$refs.formModal.open(ticket, true)
    },

    async startCustomerServiceStatus(id) {
      this.$confirm({
        message: 'Deseja iniciar o trabalho para este ticket de valor fixo?',
        button: { no: 'Não', yes: 'Sim' },
        callback: async confirm => {
          if (confirm) {
            try {
              await axios.patch(`/api/customer-service/${id}/start`)
              this.$toast.success('Ticket iniciado!')
              this.loadCustomerServices()
              this.index()
              this.loadWorkload()
            } catch (error) {
              this.$toast.error('Erro ao iniciar ticket')
            }
          }
        }
      })
    },

    async finishedCustomerService(id) {
      this.$confirm({
        message: 'Tem certeza que deseja finalizar o Ticket?',
        button: { no: 'Não', yes: 'Sim' },
        callback: async confirm => {
          if (confirm) {
            try {
              await axios.patch(`/api/customer-service/${id}/finish`)
              this.$toast.success('Ticket finalizado!')
              this.loadCustomerServices()
              this.index()
              this.loadWorkload()
            } catch (error) {
              this.$toast.error('Erro ao finalizar ticket')
            }
          }
        }
      })
    },

    async editTaskMade(task) {
      try {
        const formData = {
          started_at: task.started_at,
          finished_at: task.finished_at,
          made_at: this.formatDate(task.made_at),
          note: task.note,
          customer_id: task.customer.data.id,
          task_id: task.task.data.id,
          task_type_id: task.task_type.data.id
        }
        await axios.put(`/api/tasks_made/${task.id}`, formData)
        this.index()
        this.loadWorkload()
      } catch (error) {
        this.$toast.error('Erro ao atualizar atividade')
      }
    },

    async destroy(id) {
      this.$confirm({
        message: 'Tem certeza que deseja excluir este lançamento?',
        button: { no: 'Não', yes: 'Sim' },
        callback: async confirm => {
          if (confirm) {
            try {
              await axios.delete(`/api/tasks_made/${id}`)
              this.$toast.success('Excluído!')
              this.index()
              this.loadCustomerServices()
              this.loadWorkload()
            } catch (error) {
              this.$toast.error('Erro ao excluir')
            }
          }
        }
      })
    },

    replay(task) {
      this.$refs.formModal.open(task)
    },

    openWorkloadResume() {
      this.eventHub.$emit('user-workload-to-company-modal', this.user)
    },

    openIncurredExpense(ticket) {
      this.eventHub.$emit('incurred-expense-modal', ticket)
    },

    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return `${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}-${("0" + d.getDate()).slice(-2)}`
    },

    isToday(date) {
      const today = new Date()
      return date.getDate() === today.getDate() &&
             date.getMonth() === today.getMonth() &&
             date.getFullYear() === today.getFullYear()
    }
  }
}
</script>

<style scoped lang="sass">
.tm-v2-page
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  min-height: 100vh

.tm-v2-header
  display: flex
  justify-content: space-between
  align-items: center
  padding-bottom: 16px
  border-bottom: 1px solid rgba(0,0,0,0.05)

.tm-v2-title
  margin: 0
  font-weight: 800
  letter-spacing: -0.5px
  color: #2d3436
  font-size: 1.75rem

.tm-v2-subtitle
  color: #636e72
  font-size: 0.9rem
  margin-top: 4px
  margin-bottom: 0

.btn-new-task
  background: linear-gradient(135deg, #00b894 0%, #00cec9 100%)
  border: none
  color: white
  font-weight: 700
  padding: 10px 24px
  border-radius: 12px
  transition: all 0.3s
  &:hover
    transform: translateY(-2px)
    box-shadow: 0 8px 15px rgba(0, 184, 148, 0.25)
    color: white
</style>
